import { Typography } from "@circle-react-shared/uikit/Typography";
import { Icon } from "@circle-react/components/shared/Icon";

import { t } from "@/i18n-js/instance";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { useUpgradePlanModal } from "@/react/components/shared/UpgradePlan";
import { CANCEL_SCHEDULED_DOWNGRADE_SOURCES } from "@/react/components/shared/UpgradePlan/types";
import {
  capitalizePlanTier,
  getI18nBillingCycle,
} from "@/react/helpers/plansHelpers";
import type { CurrentPlan } from "@/react/types/CurrentPlan";

import { WarningWrapper } from "./WarningWrapper";

interface ScheduledSubscriptionBannerProps {
  currentPlan: CurrentPlan;
}

export const ScheduledSubscriptionBanner = ({
  currentPlan,
}: ScheduledSubscriptionBannerProps) => {
  const scheduledDowngradeMetadata = currentPlan?.scheduled_downgrade_metadata;
  const isPlanDowngrade = scheduledDowngradeMetadata?.plan_downgrade;
  const upgradeModal = useUpgradePlanModal();

  if (!scheduledDowngradeMetadata?.next_plan) return null;

  const nextPlanName = getI18nBillingCycle(
    scheduledDowngradeMetadata.next_plan,
  ).toLowerCase();

  const switchDate = scheduledDowngradeMetadata.switch_date;
  const titleParams = { nextPlanName, switchDate };
  const discountAmount =
    scheduledDowngradeMetadata.annual_discount_amount_with_currency;

  const handleBannerAction = () => {
    void upgradeModal.show({
      source: CANCEL_SCHEDULED_DOWNGRADE_SOURCES.SCHEDULED_DOWNGRADE_BANNER,
      showPlanDetails: false,
    });
  };

  const getBodyTranslationParams = () => {
    if (isPlanDowngrade) {
      return {
        nextPlanName: capitalizePlanTier(
          scheduledDowngradeMetadata.next_plan.tier,
        ),
        currentPlanName: capitalizePlanTier(currentPlan.tier),
      };
    }
    return {
      discountAmount,
    };
  };

  return (
    <WarningWrapper>
      <div className="flex items-start gap-4">
        <div className="mr-2 mt-[2px]">
          <Icon type="information-outline" size={24} />
        </div>
        <div className="flex-1">
          <div className="mb-1">
            <Typography.LabelMd color="text-default" weight="semibold">
              {isPlanDowngrade
                ? t(
                    "pricing_plans.downgrade_schedule_banner.plan_downgrade.title",
                    titleParams,
                  )
                : t(
                    "pricing_plans.downgrade_schedule_banner.interval_downgrade.title",
                    titleParams,
                  )}
            </Typography.LabelMd>
          </div>
          <div className="mb-3">
            <Typography.BodySm color="text-default">
              {isPlanDowngrade
                ? t(
                    "pricing_plans.downgrade_schedule_banner.plan_downgrade.body",
                    getBodyTranslationParams(),
                  )
                : t(
                    "pricing_plans.downgrade_schedule_banner.interval_downgrade.body",
                    getBodyTranslationParams(),
                  )}
            </Typography.BodySm>
          </div>
          <Button variant="secondary" onClick={handleBannerAction}>
            {isPlanDowngrade
              ? t(
                  "pricing_plans.downgrade_schedule_banner.plan_downgrade.cta_button",
                )
              : t(
                  "pricing_plans.downgrade_schedule_banner.interval_downgrade.cta_button",
                )}
          </Button>
        </div>
      </div>
    </WarningWrapper>
  );
};
